import { AddRounded, StarRounded } from '@mui/icons-material';
import {
  Box,
  ButtonBase,
  Collapse,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import { formatDollarAmount } from '../../utils/global/helper';
import useItemQuery from '../../hooks/checkouts/useItemQuery';
import { ItemWithCommissions } from '../../types/items';
import { customLineLabel } from '../../utils/checkout/customLine';

export const HEIGHT = 56;
export const SPACING = 8;

interface Props {
  item: ItemWithCommissions | null;
  index: number;
  totalCount: number;
  onAddItem: (item: ItemWithCommissions | null) => void;
  isFocused?: boolean;
}

function CheckoutItemsSearchRow({
  item,
  index,
  totalCount,
  onAddItem,
  isFocused,
}: Props) {
  const [query] = useItemQuery();
  const theme = useTheme();

  const isOnlyItem = totalCount === 1;
  const isLast = index === totalCount - 1;

  const coverUrl = item?.images?.[0]?.url;

  const renderAvatar = () => {
    if (!item) {
      return <AddRounded />;
    }

    if (coverUrl) {
      return (
        <Box
          component="img"
          src={coverUrl}
          alt={item.name}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      );
    }

    const shortenedName = item.name.slice(0, 2);
    return (
      <Typography color="text.secondary" variant="h5">
        {shortenedName}
      </Typography>
    );
  };

  const priceString = item ? formatDollarAmount(item.price || 0) : '';

  return (
    <ButtonBase
      type="button"
      onMouseDown={e => {
        e.preventDefault();
      }}
      onClick={() => {
        onAddItem(item);
      }}
      sx={{
        fontFamily: theme.typography.fontFamily,
        textAlign: 'left',
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1,
        mb: !isLast && !isOnlyItem ? `${SPACING}px` : 0,
        height: HEIGHT,
        borderRadius: '10px',
        bgcolor: isFocused ? theme.palette.accent.main : 'white',
        boxSizing: 'border-box',
        ':hover': {
          bgcolor: theme.palette.accent.main,
        },
      }}>
      <Stack direction="row" gap={1} alignItems="center">
        <Stack
          alignItems="center"
          justifyContent="center"
          bgcolor={theme.palette.lightGrey.main}
          borderRadius="10px"
          width={40}
          height={40}
          p={coverUrl ? 0 : 1}
          boxSizing="border-box"
          overflow="hidden"
          sx={{
            aspectRatio: '1',
          }}>
          {renderAvatar()}
        </Stack>

        <Typography variant="h5" noWrap>
          {!!item?.favoritedAt && (
            <StarRounded
              sx={{
                // eslint-disable-next-line no-restricted-syntax -- typography-exception: favourite-star yellow is a decorative icon colour with no palette token
                color: '#FDCC0D',
                fontSize: theme.typography.body1.fontSize,
                verticalAlign: 'text-bottom',
                mr: 0.5,
                display: 'inline-block',
              }}
            />
          )}
          {!item ? customLineLabel(query) : item.name || 'Unknown item'}
        </Typography>
      </Stack>

      <Collapse
        in={isFocused}
        timeout={theme.transitions.duration.short}
        orientation="horizontal"
        collapsedSize={priceString.length * 8}>
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ flexShrink: 0, flexGrow: 1 }}>
          {!!item && (
            <Typography variant="body2" color="text.secondary">
              {formatDollarAmount(item.price || 0)}
            </Typography>
          )}

          <KeyboardReturnRoundedIcon
            sx={{
              width: isFocused ? 'auto' : 0,
            }}
            htmlColor={theme.palette.medGrey.main}
          />
        </Stack>
      </Collapse>
    </ButtonBase>
  );
}

export default CheckoutItemsSearchRow;
